import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import { Avatar, Box, Card, Chip, IconButton, Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import Rating from "../../../shared/components/Rating";
import { STATUS } from "../utils";
import { useLocation, useNavigate } from "react-router-dom";

const WorkshopCard = ({ workshop, index, reservationServiceCount }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const status = STATUS[workshop.active ? "active" : "inactive"];
  const navigate = useNavigate();
  const location = useLocation();

  const handleEditClick = () => {
    navigate(`/admin/edit-workshop/${workshop.id}`, { state: { from: location.pathname } });
  };

  const handleDeleteClick = () => {
    // Handle delete click
  };

  return isMobile ? (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        borderRadius: 2,
        border: "1px solid #EBEDF1",
        px: 2.5,
        py: 2,
        gap: 2,
        "&:hover": { boxShadow: "0 2px 10px rgba(20,30,50,0.06)" },
        transition: "box-shadow .15s ease",
        cursor: "pointer",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction={"row"} spacing={1.5} alignItems='center'>
          <Avatar sx={{ bgcolor: "#003C59", width: 34, height: 34, fontSize: 14, fontWeight: 700 }}>{index + 1}</Avatar>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1A2233" }}>{workshop?.name}</Typography>
            <Chip
              size='small'
              label={t(`adminDashboard.${status.key}`)}
              sx={{
                bgcolor: status.bg,
                color: status.color,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
          </Box>
        </Stack>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-evenly" }}>
          <IconButton size='small' onClick={handleEditClick}>
            <EditIcon sx={{ color: "text.secondary" }} />
          </IconButton>
          <IconButton size='small' onClick={handleDeleteClick}>
            <DeleteIcon sx={{ color: "text.secondary" }} />
          </IconButton>
        </Box>
      </Box>
    </Card>
  ) : (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        borderRadius: 2,
        border: "1px solid #EBEDF1",
        px: 2.5,
        py: 2,
        gap: 2,
        "&:hover": { boxShadow: "0 2px 10px rgba(20,30,50,0.06)", borderLeft: `4px solid #00C9DB` },
        transition: "box-shadow .15s ease",
        cursor: "pointer",
      }}
      onClick={handleEditClick}
    >
      <Avatar sx={{ bgcolor: "#003C59", width: 34, height: 34, fontSize: 14, fontWeight: 700 }}>{index + 1}</Avatar>
      <Box sx={{ display: "flex", justifyContent: "space-between", flex: 1, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, gap: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1A2233" }}>{workshop?.name}</Typography>
            <Chip
              size='small'
              label={t(`adminDashboard.${status.key}`)}
              sx={{
                bgcolor: status.bg,
                color: status.color,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <PlaceIcon fontSize='small' sx={{ mr: 0.5, color: "text.secondary" }} />
            <Typography variant='body2' sx={{ color: "text.secondary", mr: 2 }}>
              {workshop?.address}
            </Typography>
            <PhoneIcon fontSize='small' sx={{ mr: 0.5, color: "text.secondary" }} />
            <Typography variant='body2' sx={{ color: "text.secondary" }}>
              + {workshop?.phone[0]?.number}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "space-evenly" }}>
          <Rating rating={workshop?.ratings} />
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant='body2' sx={{ color: "text.secondary", fontWeight: 500 }}>
              {reservationServiceCount?.reservationCount || 0}
            </Typography>
            <Typography>{t("adminDashboard.reservations")}</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant='body2' sx={{ color: "text.secondary", fontWeight: 500 }}>
              {reservationServiceCount?.serviceCount || 0}
            </Typography>
            <Typography>{t("adminDashboard.services")}</Typography>
          </Box>
          <IconButton size='small' onClick={handleEditClick}>
            <EditIcon sx={{ color: "text.secondary" }} />
          </IconButton>
          <IconButton size='small' onClick={handleDeleteClick}>
            <DeleteIcon sx={{ color: "text.secondary" }} />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
};

export default WorkshopCard;
